import React, {useEffect, useState} from 'react';
import {Alert, Box, LinearProgress, Skeleton, Stack, Tab, Tabs, Typography} from "@mui/material";
import {OrganizationRequests} from "../../api/requests/OrganizationRequests";
import ConfirmDeleteModal from "../modal/ConfirmDeleteModal";
import PageHeader from "../layouts/PageHeader";
import {useToasts} from "../../hooks/useToasts"
import OrganizationDetailsForm from "./OrganizationDetailsForm"
import DangerActionBox from "../form/DangerActionBox"
import DangerZone from "../form/DangerZone"
import {useApi} from "../../hooks/useApi"
import BillingPortalButton from "../billing/BillingPortalButton"
import CheckoutButton from "../billing/CheckoutButton"
import FormContainer from "../layouts/FormContainer"
import ApiError from "../../api/ApiError"
import {resolveNameForPlanTier} from "../../api/enum/PlanTier"
import {OrganizationStatsJson} from "../../api/json/OrganizationStatsJson"
import RedeemUpgradeCodeForm from "./RedeemUpgradeCodeForm"
import ErrorMessage from "../error/ErrorMessage"
import {useSearchParams} from "react-router-dom"
import {useBreadcrumbs} from "../layouts/Nav/BreadcrumbsProvider"
import {NAV_LABELS} from "../../library/NavLabels"
import ApiKeysTab from "../apikeys/ApiKeysTab"

type OrgSettingsTab = "general" | "billing" | "advanced" | "api-keys"

const ORG_SETTINGS_TABS: OrgSettingsTab[] = ["general", "billing", "advanced", "api-keys"]

function CurrentOrganizationPage() {
    useBreadcrumbs([{label: NAV_LABELS.ORGANIZATION_SETTINGS}])

    const [error, setError] = useState<ApiError>()
    const [confirmingDeleteRequest, setConfirmingDeleteRequest] = useState(false)
    const [deleteEmailSent, setDeleteEmailSent] = useState(false)
    const [stats, setStats] = useState<OrganizationStatsJson>()

    const [searchParams, setSearchParams] = useSearchParams()
    const tabParam = searchParams.get("tab") as OrgSettingsTab | null
    const activeTab: OrgSettingsTab = tabParam && ORG_SETTINGS_TABS.includes(tabParam) ? tabParam : "general"

    const toasts = useToasts()
    const {currentOrganization, setCurrentOrganization} = useApi()

    useEffect(() => {
        if (!currentOrganization) return
        OrganizationRequests.get(currentOrganization.id)
            .then(setCurrentOrganization)
            .catch(setError)
    }, [])

    useEffect(() => {
        if (!currentOrganization) return
        OrganizationRequests.getStats(currentOrganization.id)
            .then(setStats)
            .catch(toasts.showError)
    }, [])

    const reloadOrganization = () => {
        if (!currentOrganization) return
        OrganizationRequests.get(currentOrganization.id)
            .then(setCurrentOrganization)
            .catch(toasts.showError)
    }

    const handleDelete = () => {
        setConfirmingDeleteRequest(true)
        OrganizationRequests.delete(currentOrganization!.id)
            .then(() => setDeleteEmailSent(true))
            .catch(toasts.showError)
            .finally(() => setConfirmingDeleteRequest(false))
    }

    if (error) {
        return <ErrorMessage error={error}/>
    }

    if (!currentOrganization) {
        return (
            <Box sx={{maxWidth: 450}}>
                <Skeleton height={60}/>
                <Skeleton height={60}/>
                <Skeleton width={100} height={40}/>
            </Box>
        )
    }

    return (
        <>
            <PageHeader pageTitle="Organization Settings"/>

            <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 3}}>
                <Tabs value={activeTab} onChange={(_, value) => {
                    const next = value as OrgSettingsTab
                    setSearchParams(next === "general" ? {} : {tab: next}, {replace: true})
                }}>
                    <Tab label="General" value="general"/>
                    <Tab label="Billing" value="billing"/>
                    <Tab label="API Keys" value="api-keys"/>
                    <Tab label="Advanced" value="advanced"/>
                </Tabs>
            </Box>

            {activeTab === "general" &&
                <FormContainer>
                    <OrganizationDetailsForm organization={currentOrganization}
                                             didSaveDetails={setCurrentOrganization}
                                             didAddLogo={setCurrentOrganization}
                    />
                </FormContainer>
            }

            {activeTab === "billing" &&
                <Stack spacing={5} sx={{maxWidth: 520}}>
                    <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h3">Plan</Typography>
                            {!currentOrganization.billingDetails &&
                                <CheckoutButton>Upgrade</CheckoutButton>
                            }
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{
                                display: 'inline-flex',
                                px: 2,
                                py: 0.75,
                                borderRadius: '6px',
                                bgcolor: currentOrganization.billingDetails ? '#f3f2ff' : '#f5f5f5',
                                color: currentOrganization.billingDetails ? '#4c49c1' : '#666',
                            }}>
                                <Typography sx={{fontWeight: 600, fontSize: '1.1rem'}}>
                                    {currentOrganization.billingDetails
                                        ? resolveNameForPlanTier(currentOrganization.billingDetails.planTier)
                                        : "Free"}
                                </Typography>
                            </Box>
                            {currentOrganization.billingDetails &&
                                <BillingPortalButton variant="text" sx={{fontSize: '0.85rem'}}>
                                    Manage Plan
                                </BillingPortalButton>
                            }
                        </Stack>
                        {currentOrganization.upgradeCodeRedemption &&
                            <Typography variant="body2" color="text.secondary" sx={{fontStyle: "italic"}}>
                                Upgrade code applied
                            </Typography>
                        }
                    </Stack>

                    {stats &&
                        <Stack spacing={4}>
                            <Typography variant="h3">Usage</Typography>

                            <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2">Rows</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {stats.exampleCount.toLocaleString()} of {currentOrganization.usageLimits.numberOfRows.toLocaleString()}
                                    </Typography>
                                </Stack>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min((stats.exampleCount / currentOrganization.usageLimits.numberOfRows) * 100, 100)}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        bgcolor: '#ececf1',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 4,
                                            bgcolor: stats.exampleCount >= currentOrganization.usageLimits.numberOfRows
                                                ? '#b02323'
                                                : '#4c49c1',
                                        },
                                    }}
                                />
                            </Stack>

                            <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2">Team Members</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {stats.userCount.toLocaleString()} of {currentOrganization.usageLimits.numberOfUsers.toLocaleString()}
                                    </Typography>
                                </Stack>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min((stats.userCount / currentOrganization.usageLimits.numberOfUsers) * 100, 100)}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        bgcolor: '#ececf1',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 4,
                                            bgcolor: stats.userCount >= currentOrganization.usageLimits.numberOfUsers
                                                ? '#b02323'
                                                : '#4c49c1',
                                        },
                                    }}
                                />
                            </Stack>
                        </Stack>
                    }

                    <RedeemUpgradeCodeForm organization={currentOrganization} didRedeem={reloadOrganization}/>
                </Stack>
            }

            {activeTab === "api-keys" && <ApiKeysTab/>}

            {activeTab === "advanced" &&
                <DangerZone>
                    {deleteEmailSent
                        ? <Alert severity="success">An email has been sent to confirm deletion.</Alert>
                        : currentOrganization.billingDetails?.activePlan
                            ? <Typography variant="body1">Deletion not available while on an active plan.</Typography>
                            : <DangerActionBox title="Delete this organization"
                                               buttonLabel="Delete"
                                               description="All your projects will be deleted."
                                               onAction={() => setConfirmingDeleteRequest(true)}
                                               actionPending={confirmingDeleteRequest}/>
                    }
                </DangerZone>
            }

            <ConfirmDeleteModal
                heading={`Delete ${currentOrganization.name} entirely`}
                body="If you are sure you want to delete this organization and all its data, we'll send a link to verify."
                deleteButtonTitle="Continue"
                open={confirmingDeleteRequest}
                setOpen={setConfirmingDeleteRequest}
                confirmedDelete={handleDelete}
            />
        </>
    )
}

export default CurrentOrganizationPage;
